import React from 'react';
import { SettingOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import NavRailButton from 'components/molecules/NavV2/NavRailButton';
import HomeOutline from 'assets/svg/HomeOutline';
import GridDots from 'assets/svg/GridDots';
import Funnel from 'assets/svg/Funnel';
import { isAdminUser } from 'constants/admin';
import { POWERED_BY_FOOTER_HEIGHT_PX } from 'config/constants';
import { toAbsolute } from 'utils/routeResolution';

// Same literal Navbar.tsx uses for its Settings link; the gear replaces it under V2.
const ADMIN_PATH = '/admin';

interface Props {
    homePath?: string;
    isNavPanelOpen: boolean;
    isFilterPanelOpen: boolean;
    /** Routes that render no global filter keep the funnel in place, greyed out. */
    isFilterDisabled: boolean;
    onToggleNavPanel: () => void;
    onToggleFilterPanel: () => void;
}

// 64px icon rail pinned under the V2 header. Owns no panel state of its own — MainContainer
// keeps the navigation panel and the global filter mutually exclusive.
function NavRail({
    homePath,
    isNavPanelOpen,
    isFilterPanelOpen,
    isFilterDisabled,
    onToggleNavPanel,
    onToggleFilterPanel,
}: Props) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // Same prefix test App.tsx uses to gate the admin routes, so every /admin/* screen lights the gear
    const isAdminRoute = pathname?.startsWith(ADMIN_PATH) === true;
    // `homePath` arrives bare from the tenant config on legacy tenants and absolute from
    // `resolveHomePath`; normalise once so both the highlight and the click agree.
    const homeRoute = homePath ? toAbsolute(homePath) : '/';

    return (
        <div
            className="nav-rail-width fixed left-0 z-[50] flex flex-col items-center gap-2 border-r border-filterV2Border bg-white pt-5"
            style={{ top: 'var(--navbar-v2-height)', bottom: POWERED_BY_FOOTER_HEIGHT_PX }}
        >
            <NavRailButton
                label="Home"
                icon={<HomeOutline />}
                isActive={pathname === homeRoute}
                onClick={() => navigate(homeRoute)}
            />
            <NavRailButton
                label="Navigation"
                icon={<GridDots />}
                isActive={isNavPanelOpen}
                onClick={onToggleNavPanel}
            />
            <NavRailButton
                label="Global Filter"
                icon={<Funnel />}
                isActive={isFilterPanelOpen}
                isDisabled={isFilterDisabled}
                onClick={onToggleFilterPanel}
            />
            {isAdminUser() && (
                <NavRailButton
                    label="Settings"
                    icon={<SettingOutlined className="text-[18px]" />}
                    isActive={isAdminRoute}
                    onClick={() => navigate(ADMIN_PATH)}
                />
            )}
        </div>
    );
}

export default NavRail;
